import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ManageCookiesLink } from "../components/CookieConsent";
import { ArrowRight, ScanLine, Banknote, MapPin, CalendarDays, Ticket, Loader2 } from "lucide-react";
import { Logo } from "../components/Logo";
import { eventsApi, formatAuDate, centsToAUD, coverImageUrl } from "../lib/events";

function priceFromCents(event) {
  const tt = event.ticket_types || [];
  if (!tt.length) return null;
  const paid = tt.filter((t) => (t.price_cents || 0) > 0);
  if (paid.length === 0) return 0;
  return Math.min(...paid.map((t) => t.price_cents));
}

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxjb25jZXJ0JTIwY3Jvd2QlMjBlbmVyZ2V0aWN8ZW58MHx8fHwxNzgzMTI0NDA5fDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const [upcoming, setUpcoming] = useState(null);
  const [upcomingError, setUpcomingError] = useState(false);

  useEffect(() => {
    let alive = true;
    eventsApi
      .publicList()
      .then((data) => {
        if (!alive) return;
        const now = Date.now();
        const filtered = (data || [])
          .filter((e) => {
            const t = new Date(e.start_at).getTime();
            return !Number.isNaN(t) && t > now - 24 * 60 * 60 * 1000;
          })
          .sort((a, b) => new Date(a.start_at) - new Date(b.start_at))
          .slice(0, 6);
        setUpcoming(filtered);
      })
      .catch(() => {
        if (alive) setUpcomingError(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div
      className="min-h-screen bg-[var(--gt-bg-marketing)] text-[var(--gt-ink)]"
      data-testid="landing-page"
    >
      {/* Nav */}
      <nav className="gt-glass-nav sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <Logo size="md" />
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/events" data-testid="nav-browse-events-link" className="gt-btn-ghost text-sm">
              Browse events
            </Link>
            <Link to="/pricing" data-testid="nav-pricing-link" className="gt-btn-ghost text-sm">
              Pricing
            </Link>
            <Link to="/who-we-are" data-testid="nav-who-we-are-link" className="gt-btn-ghost text-sm hidden sm:inline-flex">
              Who We Are
            </Link>
            <Link to="/resend" data-testid="nav-resend-link" className="gt-btn-ghost text-sm hidden sm:inline-flex">
              Lost ticket?
            </Link>
            <Link to="/login" data-testid="nav-login-btn" className="gt-btn-ghost text-sm">
              Log in
            </Link>
            <Link to="/signup" data-testid="nav-signup-btn" className="gt-btn-primary text-sm py-2">
              Sign up
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pt-16 sm:pt-24 pb-20 sm:pb-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center">
          <div className="md:col-span-7">
            <div className="gt-label mb-6" data-testid="hero-eyebrow">
              Made in Australia · AUD · GST-Ready
            </div>
            <h1 className="font-heading font-black tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl mb-6">
              Sell tickets to the whole of Australia
              <span className="text-[var(--gt-accent)]">.</span>
            </h1>
            <p className="text-[var(--gt-ink-2)] text-base sm:text-lg leading-relaxed max-w-xl mb-10">
              Getickets is a lean, Australian-focused ticketing platform for organisers who
              want fair fees, fast payouts in AUD, and door scanning that just works.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/signup" className="gt-btn-primary" data-testid="hero-create-event-btn">
                Create an event <ArrowRight size={16} />
              </Link>
              <Link to="/login" className="gt-btn-secondary" data-testid="hero-login-btn">
                I already have an account
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-[var(--gt-ink-2)]">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[var(--gt-accent)]" />
                Stripe-secured checkout
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[var(--gt-ink)]" />
                No lock-in contracts
              </div>
            </div>
          </div>

          <div className="md:col-span-5 relative">
            <div className="relative rounded-sm overflow-hidden border-2 border-[var(--gt-ink)] shadow-[8px_8px_0px_0px_var(--gt-ink)]">
              <img
                src={HERO_IMAGE}
                alt="Energetic Australian concert crowd"
                className="w-full h-[420px] object-cover"
                data-testid="hero-image"
              />
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur border border-[var(--gt-line)] rounded-sm p-4 flex items-center justify-between">
                <div>
                  <div className="gt-label mb-1">Featured</div>
                  <div className="font-heading font-bold text-lg">Sydney Harbour Jazz</div>
                  <div className="text-xs text-[var(--gt-ink-2)]">14/03/2026 · Circular Quay, NSW</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-[var(--gt-ink-3)]">From</div>
                  <div className="font-heading font-black text-xl">$49 AUD</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              n: "01",
              icon: MapPin,
              title: "Built for Australia",
              body: "AUD from day one, DD/MM/YYYY dates, AEST/AEDT timezones, and GST-ready reporting for your BAS.",
            },
            {
              n: "02",
              icon: Banknote,
              title: "Fast, transparent payouts",
              body: "Direct settlement to your Australian bank account. See every fee upfront — no surprises.",
            },
            {
              n: "03",
              icon: ScanLine,
              title: "Door scanning that works",
              body: "Every ticket is a signed QR. Scan from any phone browser. No app install required.",
            },
          ].map(({ n, icon: Icon, title, body }) => (
            <div key={n} className="gt-card relative" data-testid={`value-prop-${n}`}>
              <div className="flex items-start justify-between mb-6">
                <span className="font-mono text-xs text-[var(--gt-ink-3)]">{n}</span>
                <Icon size={28} strokeWidth={1.5} className="text-[var(--gt-accent)]" />
              </div>
              <h3 className="font-heading text-2xl font-bold mb-2 tracking-tight">{title}</h3>
              <p className="text-[var(--gt-ink-2)] text-sm leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Upcoming Events preview */}
      <section
        className="max-w-7xl mx-auto px-6 sm:px-10 pb-24"
        data-testid="upcoming-events-section"
        aria-labelledby="upcoming-events-heading"
      >
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <div className="gt-label mb-3">On sale now</div>
            <h2
              id="upcoming-events-heading"
              className="font-heading text-3xl sm:text-5xl font-black tracking-tighter leading-none"
            >
              Upcoming events
            </h2>
          </div>
          <Link
            to="/events"
            data-testid="browse-all-events-btn"
            className="gt-btn-secondary self-start sm:self-end"
          >
            Browse all events <ArrowRight size={16} />
          </Link>
        </div>

        {upcoming === null && !upcomingError && (
          <div
            className="flex items-center gap-3 text-[var(--gt-ink-2)]"
            data-testid="upcoming-events-loading"
          >
            <Loader2 size={16} className="animate-spin" />
            <span className="text-sm">Loading upcoming events…</span>
          </div>
        )}

        {upcomingError && (
          <div
            className="border border-[var(--gt-line)] rounded-sm p-6 text-sm text-[var(--gt-ink-2)]"
            data-testid="upcoming-events-error"
          >
            Couldn’t load events right now. Please refresh in a moment.
          </div>
        )}

        {upcoming && upcoming.length === 0 && (
          <div
            className="border border-[var(--gt-line)] rounded-sm p-10 text-center bg-white"
            data-testid="upcoming-events-empty"
          >
            <div className="gt-label mb-3">Nothing scheduled</div>
            <h3 className="font-heading text-2xl font-bold tracking-tight mb-2">
              No public events are available yet.
            </h3>
            <p className="text-[var(--gt-ink-2)] text-sm max-w-md mx-auto">
              Check back soon — organisers publish new events across Australia every day.
            </p>
          </div>
        )}

        {upcoming && upcoming.length > 0 && (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
            data-testid="upcoming-events-grid"
          >
            {upcoming.map((e) => {
              const cover = coverImageUrl(e);
              const pc = priceFromCents(e);
              const isFree = pc === 0;
              return (
                <Link
                  key={e.id}
                  to={`/e/${e.slug}`}
                  data-testid={`upcoming-event-card-${e.slug}`}
                  className="group flex flex-col border border-[var(--gt-line)] rounded-sm bg-white overflow-hidden hover:border-[var(--gt-ink)] transition-colors"
                >
                  <div className="relative aspect-[16/9] bg-[var(--gt-bg-surface)] overflow-hidden">
                    {cover ? (
                      <img
                        src={cover}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-[var(--gt-ink-3)]">
                        <CalendarDays size={40} strokeWidth={1.5} />
                      </div>
                    )}
                    <div className="absolute top-3 left-3">
                      <span
                        className={`inline-block text-[10px] font-bold tracking-[0.18em] uppercase px-2 py-1 rounded-sm ${
                          isFree
                            ? "bg-[var(--gt-ink)] text-white"
                            : "bg-white/95 text-[var(--gt-ink)] border border-[var(--gt-line)]"
                        }`}
                      >
                        {isFree ? "Free" : "Paid"}
                      </span>
                    </div>
                  </div>
                  <div className="p-5 flex flex-col gap-2 flex-1">
                    <h3 className="font-heading text-lg font-bold tracking-tight leading-tight group-hover:text-[var(--gt-accent)] transition-colors line-clamp-2">
                      {e.title}
                    </h3>
                    <div className="flex flex-col gap-1 text-xs text-[var(--gt-ink-2)]">
                      <span className="inline-flex items-center gap-1.5">
                        <CalendarDays size={13} /> {formatAuDate(e.start_at)}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin size={13} /> {e.venue_name}, {e.state}
                      </span>
                    </div>
                    <div className="mt-auto flex items-end justify-between pt-3 border-t border-[var(--gt-line)]">
                      <div>
                        <div className="text-[10px] tracking-widest uppercase text-[var(--gt-ink-3)]">
                          {isFree ? "Entry" : "From"}
                        </div>
                        <div className="font-heading font-black text-lg">
                          {isFree ? "Free" : centsToAUD(pc)}
                        </div>
                      </div>
                      <span className="text-xs font-medium inline-flex items-center gap-1 group-hover:text-[var(--gt-accent)]">
                        {isFree ? "Register" : "Get tickets"} <ArrowRight size={13} />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="bg-[var(--gt-ink)] text-[var(--gt-bg-marketing)]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <div>
              <Logo inverse size="xl" />
              <div className="mt-3 text-xs tracking-[0.25em] uppercase text-[var(--gt-ink-3)]">
                Australian Event Ticketing · MVP
              </div>
            </div>
            <div className="flex flex-col gap-2 text-sm">
              <Link to="/signup" className="hover:text-[var(--gt-accent)] transition-colors" data-testid="footer-signup-link">Sign up as organiser</Link>
              <Link to="/login" className="hover:text-[var(--gt-accent)] transition-colors" data-testid="footer-login-link">Log in</Link>
              <Link to="/who-we-are" className="hover:text-[var(--gt-accent)] transition-colors" data-testid="footer-who-we-are-link">Who we are</Link>
              <Link to="/privacy" className="hover:text-[var(--gt-accent)] transition-colors" data-testid="footer-privacy-link">Privacy</Link>
              <ManageCookiesLink className="text-sm" />
              <a className="hover:text-[var(--gt-accent)] transition-colors" href="mailto:admin@getickets.com.au">admin@getickets.com.au</a>
            </div>
          </div>
          <div className="mt-12 pt-6 border-t border-white/10 text-xs text-[var(--gt-ink-3)]" data-testid="footer-copyright">
            © {new Date().getFullYear()} Getickets · Made for Aussie organisers. Powered by{" "}
            <a
              href="https://getupsolutions.com.au/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[var(--gt-accent)]"
              data-testid="footer-powered-by-link"
            >
              www.getupsolutions.com.au
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
